import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, CheckCircle, TrendingUp, Users, Target, Download, Save, Star } from "lucide-react";
import { toast } from "sonner";

interface JobAnalysisData {
  companyName: string;
  jobTitle: string;
  companyMission: string;
  responsibilities: string[];
  requirements: string[];
  skills: string[];
}

interface JobWizardStep5Props {
  onSave: (jobData: any) => void;
  onBack: () => void;
  analysisData: JobAnalysisData | null;
  selectedResponsibilities: string[];
  selectedRequirements: string[];
  selectedSkills: string[];
}

export function JobWizardStep5({ 
  onSave, 
  onBack, 
  analysisData, 
  selectedResponsibilities, 
  selectedRequirements, 
  selectedSkills 
}: JobWizardStep5Props) {
  const [matchScore, setMatchScore] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (analysisData) {
      // Calculate weighted match score
      const responsibilityScore = (selectedResponsibilities.length / analysisData.responsibilities.length) * 40;
      const requirementScore = (selectedRequirements.length / analysisData.requirements.length) * 35;
      const skillsScore = (selectedSkills.length / analysisData.skills.length) * 25;
      
      const totalScore = Math.round(responsibilityScore + requirementScore + skillsScore);
      setMatchScore(totalScore);
    }
  }, [analysisData, selectedResponsibilities, selectedRequirements, selectedSkills]);

  const handleSave = async () => {
    if (!analysisData) return;

    setIsSaving(true);
    try {
      const jobData = {
        companyName: analysisData.companyName,
        jobTitle: analysisData.jobTitle,
        companyMission: analysisData.companyMission,
        selectedResponsibilities,
        selectedRequirements,
        selectedSkills,
        allResponsibilities: analysisData.responsibilities,
        allRequirements: analysisData.requirements,
        allSkills: analysisData.skills,
        matchScore
      };

      await onSave(jobData);
      toast.success("Job analysis saved to your library!");
    } catch (error) {
      console.error('Error saving job:', error);
      toast.error("Failed to save job analysis. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-600 bg-green-50 border-green-200";
    if (score >= 60) return "text-yellow-600 bg-yellow-50 border-yellow-200";
    return "text-red-600 bg-red-50 border-red-200";
  };

  const getScoreIcon = (score: number) => {
    if (score >= 80) return <Star className="w-6 h-6 text-green-600" />;
    if (score >= 60) return <TrendingUp className="w-6 h-6 text-yellow-600" />;
    return <Target className="w-6 h-6 text-red-600" />;
  };

  const getRecommendations = (score: number) => {
    if (score >= 80) {
      return [
        "Excellent match! You meet most of the job requirements.",
        "Focus on highlighting your relevant experience in your resume.",
        "Consider applying with confidence to this position.",
        "Prepare specific examples that demonstrate your matching skills."
      ];
    } else if (score >= 60) {
      return [
        "Good match with room for improvement.",
        "Consider gaining experience in unselected requirements.",
        "Highlight transferable skills that relate to missing requirements.",
        "Network with professionals in similar roles to learn more."
      ];
    } else {
      return [
        "This role may be challenging but not impossible.",
        "Focus on developing skills in the unselected areas.",
        "Consider similar roles that might be a better match.",
        "Use this as a learning opportunity to identify growth areas."
      ];
    }
  };

  if (!analysisData) return null;

  return (
    <div className="min-h-screen bg-blue-100 p-6">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg">
        <div className="bg-gradient-to-r from-accent-light/30 to-primary-light/20 border-b border-border shadow-sm rounded-t-lg backdrop-blur-sm">
          <div className="p-6 border-b border-border-light">
            <div className="flex items-center justify-between">
              <div>
                <Badge variant="outline" className="mb-2">Step 5 of 5</Badge>
                <h1 className="flex items-center gap-2 text-xl font-semibold text-foreground">
                  <CheckCircle className="w-5 h-5 text-success" />
                  Job Analysis Complete
                </h1>
                <p className="text-muted-foreground">
                  Review your match score and save this analysis to your library
                </p>
              </div>
            </div>
            <div className="mt-4">
              <Progress value={100} className="w-full" />
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Company & Position Info */}
          <Card className="border-border-light shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Position Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Company</p>
                  <p className="text-lg font-semibold">{analysisData.companyName}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Position</p>
                  <p className="text-lg font-semibold">{analysisData.jobTitle}</p>
                </div>
              </div>
              {analysisData.companyMission && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-muted-foreground mb-2">Mission & Role</p>
                  <p className="text-sm leading-relaxed">{analysisData.companyMission}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Match Score */}
          <Card className={`border-2 ${getScoreColor(matchScore)}`}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {getScoreIcon(matchScore)}
                Overall Match Score
              </CardTitle>
              <CardDescription>
                Based on your selected qualifications and experience
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="text-4xl font-bold">{matchScore}%</div>
                <div className="flex-1">
                  <Progress value={matchScore} className="h-3" />
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-4 mt-6">
                <div className="text-center p-3 bg-primary/5 rounded-lg">
                  <p className="text-2xl font-bold text-primary">{selectedResponsibilities.length}</p>
                  <p className="text-xs text-muted-foreground">of {analysisData.responsibilities.length} Responsibilities</p>
                </div>
                <div className="text-center p-3 bg-warning/5 rounded-lg">
                  <p className="text-2xl font-bold text-warning">{selectedRequirements.length}</p>
                  <p className="text-xs text-muted-foreground">of {analysisData.requirements.length} Requirements</p>
                </div>
                <div className="text-center p-3 bg-success/5 rounded-lg">
                  <p className="text-2xl font-bold text-success">{selectedSkills.length}</p>
                  <p className="text-xs text-muted-foreground">of {analysisData.skills.length} Skills</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recommendations */}
          <Card className="border-border-light shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5" />
                Recommendations
              </CardTitle>
              <CardDescription>
                Actionable insights based on your match score
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {getRecommendations(matchScore).map((recommendation, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Badge variant="outline" className="mt-0.5 shrink-0">
                      {index + 1}
                    </Badge>
                    <p className="text-sm leading-relaxed">{recommendation}</p>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex justify-between pt-6 border-t border-border-light">
            <Button variant="outline" onClick={onBack}>
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <div className="flex gap-3">
              <Button variant="outline" size="lg">
                <Download className="w-4 h-4" />
                Export PDF
              </Button>
              <Button 
                variant="gradient" 
                size="lg" 
                onClick={handleSave}
                disabled={isSaving}
              >
                <Save className="w-4 h-4" />
                {isSaving ? "Saving..." : "Save to Library"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}